import {
  GainType,
  IFullUnit,
  ISelectedUnit,
  ISelectedUpgrade,
  ISpecialRule,
  IUnit,
  IUpgradeGains,
  IUpgradeGainsItem,
  IUpgradeGainsRule,
  IUpgradeOption,
  IUpgradePackage,
  IUpgradeSection,
  ValidationErrorType,
  ValidationResult
} from "@/data/types";
import { groupBy, orderBy, partition, sortBy } from "lodash-es";
import { nanoid } from "nanoid";
import ArmyBookRule from "./armyBook/ArmyBookRule";
import ArmyBookWeapon from "./armyBook/ArmyBookWeapon";
import BalanceValidationService from "./balval/BalanceValidationService";
import PackageValidator from "./balval/PackageValidator";
import CalcService from "./calc/calcService";
import EquipmentService from "./EquipmentService";
import {
  calculateUnitTotal,
  getUnitRules,
  getUnitWeapons,
  isHero,
  isItem,
  isRule,
  isWeapon,
  makeCopy
} from "./Helpers";

export default class UnitService {
  public static getSelected(selectedUnitId: string, units: ISelectedUnit[]): ISelectedUnit {
    return selectedUnitId ? units.find((u) => u.selectionId === selectedUnitId) : null;
  }

  public static getAllEquipment(unit: { loadout: IUpgradeGains[] }) {
    const items = unit.loadout.filter((e) => e.type === GainType.Item);
    const itemContent = items.flatMap((i: IUpgradeGainsItem) => i.content);
    return unit.loadout.concat(itemContent);
  }

  public static getUpgradeRules(unit: { selectedUpgrades: ISelectedUpgrade[] }): IUpgradeGainsRule[] {
    return (unit.selectedUpgrades ?? [])
      .map((x) => x.option)
      .reduce((value, option) => value.concat(option.gains), [])
      .filter((x) => x.type === GainType.Rule) as IUpgradeGainsRule[];
  }

  public static getAllUpgradedRules(unit: {
    loadout: IUpgradeGains[];
    selectedUpgrades: ISelectedUpgrade[];
  }): IUpgradeGainsRule[] {
    const rules = this.getUpgradeRules(unit) || [];
    const rulesFromItems =
      unit.loadout
        ?.filter((x) => isItem(x) && !x.isModel)
        .reduce((rules, u: IUpgradeGainsItem) => {
          const ruleContent = u.content.filter((c) => isRule(c)).map((x) => ({ ...x, count: u.count }));
          return rules.concat(ruleContent);
        }, []) || [];

    return rules.concat(rulesFromItems) as IUpgradeGainsRule[];
  }

  public static getAllRules(unit: ISelectedUnit): IUpgradeGainsRule[] {
    return getUnitRules(unit).concat(this.getAllUpgradedRules(unit)) as IUpgradeGainsRule[];
  }

  public static createUnitFromDefinition(unit: IUnit): ISelectedUnit {
    return {
      ...unit,
      selectionId: nanoid(5),
      selectedUpgrades: [],
      combined: false,
      joinToUnit: null,
      weapons: unit.weapons.map((eqp) => ({
        ...eqp,
        count: eqp.count || unit.size // Add count to unit size if not already present
      })),
      loadout: [],
      xp: 0,
      traits: [],
      notes: null
    };
  }

  public static mergeCombinedUnit(unit: ISelectedUnit, attached: ISelectedUnit): ISelectedUnit {
    if (!attached) return unit;
    return {
      ...unit,
      size: unit.size + attached.size,
      loadout: unit.loadout.concat(attached.loadout),
      selectedUpgrades: unit.selectedUpgrades.concat(attached.selectedUpgrades)
    };
  }

  public static getFullUnitList(input: ISelectedUnit[], combine: boolean): IFullUnit[] {
    const units = (input ?? []).map((u) => makeCopy(u));

    const result = units
      .filter((x: ISelectedUnit) => !x.joinToUnit)
      .map((unit: ISelectedUnit) => {
        const attachedUnits: ISelectedUnit[] = input.filter((u) => u.joinToUnit === unit.selectionId);
        const [heroes, joined]: [ISelectedUnit[], ISelectedUnit[]] = partition(attachedUnits, (u) =>
          isHero(u)
        );

        const unitSize = joined.reduce((size, u) => size + u.size, unit.size);
        const unitPoints = joined.reduce((cost, u) => cost + calculateUnitTotal(u), calculateUnitTotal(unit));
        const unitPointsAll = attachedUnits.reduce(
          (cost, u) => cost + calculateUnitTotal(u),
          calculateUnitTotal(unit)
        );

        const finalUnit = combine ? UnitService.mergeCombinedUnit(unit, joined[0]) : unit;
        return {
          unitSize,
          unitPoints,
          unitPointsAll,
          unit: finalUnit,
          joined: joined[0], // Combined units can only have one joined...
          hero: heroes[0],
          hasJoined: attachedUnits.length > 0
        };
      });

    return sortBy(result, (x) => x.unit.sortId);
  }

  public static getGroupedDisplayUnits(input: IFullUnit[]) {
    const unitAsKey = (fullUnit: IFullUnit, unit: ISelectedUnit) => {
      return {
        heroBreaker: fullUnit.hero ? nanoid() : undefined,
        id: unit.id,
        customName: unit.customName,
        // joinToUnit: unit.joinToUnit,
        size: unit.size,
        upgrades: unit.selectedUpgrades.map((x) => `${x.upgrade.uid}${x.option.uid}`).sort(),
        loadout: unit.loadout.map((x) => `${x.id}${x.count}`).sort(),
        traits: unit.traits,
        xp: unit.xp || 0
      };
    };

    return groupBy(input, (u) => JSON.stringify(unitAsKey(u, u.unit)));
  }

  public static getTough(unit: {
    rules: ISpecialRule[];
    loadout?: IUpgradeGains[];
    selectedUpgrades?: ISelectedUpgrade[];
    size: number;
  }) {
    if (!unit || !unit.rules) return 1;
    // TODO: Why were items excluded here?
    const rules = getUnitRules(unit); //, !includeItems);
    let baseTough: number = 0;

    baseTough += rules.reduce((tough, rule) => {
      if (rule.name === "Tough") {
        tough += parseInt(rule.rating as any);
      }
      return tough;
    }, 0);

    const upgrades = UnitService.getAllUpgradedRules(unit as any);
    baseTough += upgrades.reduce((tough, { count, name, rating }) => {
      if (name === "Tough" && (count || 1) === unit.size) {
        tough += parseInt(rating);
      }
      return tough;
    }, 0);
    return baseTough || 1;
  }

  public static validateUnit(
    unit: IUnit,
    upgradePackages: IUpgradePackage[],
    calc: CalcService,
    balanceService: BalanceValidationService,
    packageValidator: PackageValidator,
    skipCalc = false
  ) {
    try {
      let hasCustomRule = false;
      let hasBalanceInvalid = false;

      unit.rules = unit.rules.filter((x) => x); // Remove empty?
      const unitCost = skipCalc ? unit.cost : calc.calculateUnitCost(unit);
      const cost = skipCalc ? unit.cost : CalcService.round(unitCost);
      const tough = UnitService.getTough(unit);

      // Balance validation

      const limits = BalanceValidationService.getLimitsForUnit(unit);

      const balvalUnit = makeCopy(unit);
      const balvalPackages = makeCopy(unit.upgrades.map((x) => upgradePackages.find((pkg) => pkg.uid === x)));

      const validationResult: ValidationResult = packageValidator.validatePackages(
        balvalUnit,
        balvalPackages
      );

      const {
        valid: unitBaseValid,
        woundsValid,
        customRulesValid,
        sizeValid,
        meleeValid
      } = balanceService.getUnitBaseValid(unit, tough, limits);

      if (
        !woundsValid ||
        !sizeValid ||
        !meleeValid ||
        validationResult.errors.some((x) => x.type !== ValidationErrorType.customRule)
      ) {
        hasBalanceInvalid = true;
      }
      if (
        !customRulesValid ||
        validationResult.errors.some((x) => x.type === ValidationErrorType.customRule)
      ) {
        hasCustomRule = true;
      }

      return {
        ...unit,
        cost,
        valid: unitBaseValid && validationResult.valid,
        hasCustomRule,
        hasBalanceInvalid
      };
    } catch (e) {
      console.error("Error validating unit", { e, unit: unit.name });
      return unit;
    }
  }

  public static validateUnits(
    units: IUnit[],
    upgradePackages: IUpgradePackage[],
    calc: CalcService,
    balanceService: BalanceValidationService,
    packageValidator: PackageValidator,
    skipCalc = false
  ) {
    // Foo
    return units.map((unit) =>
      this.validateUnit(unit, upgradePackages, calc, balanceService, packageValidator, skipCalc)
    );
  }

  public static fixUnit(unit: IUnit) {
    delete (unit as any).costMode;
    delete (unit as any).costModeAutomatic;
    delete (unit as any).splitPageNumber;

    if (!unit.weapons) {
      unit.weapons = makeCopy((unit as any).equipment);
      unit.rules = makeCopy((unit as any).specialRules);
      delete (unit as any).equipment;
      delete (unit as any).specialRules;
    }

    const weapons = unit.weapons.map((weapon) => {
      const weaponObject = ArmyBookWeapon.FromObject({
        ...weapon,
        name: weapon.name || "NO NAME" // Why do weapons with no name exist?
      });
      // Capture the count digit from the name
      const countMatch = /^(\d+)x\s/.exec(weapon.label);
      const count = weapon.count
        ? weapon.count * unit.size
        : countMatch
          ? parseInt(countMatch[1]) * unit.size
          : unit.size;
      return {
        ...weapon,
        label: weaponObject.toString(),
        name: weaponObject.name,
        count: count,
        originalCount: count,
        type: GainType.Weapon,
        specialRules: weaponObject.specialRules.map((r) => {
          const rule = ArmyBookRule.Parse(r);
          return { ...r, label: rule.toString() };
        })
      };
    });

    return {
      ...unit,
      // Transform this into a collection of upgrades
      weapons: orderBy(weapons, [(weapon) => weapon.range || 0, "name"], ["desc", "asc"]),
      rules: unit.rules.map((r) => {
        const rule = ArmyBookRule.Parse(r);
        return { ...r, label: rule.toString() };
      }),
      items: (unit.items || []).map((item) => ({
        ...item,
        count: item.count ? item.count * unit.size : unit.size,
        content: item.content.map((c) => {
          if (!isWeapon(c)) return c;
          const weaponObject = ArmyBookWeapon.FromObject(c);
          return {
            ...c,
            label: weaponObject.toString(),
            specialRules: weaponObject.specialRules,
            count: c.count ? c.count * unit.size : unit.size
          };
        })
      }))
      //disabledUpgradeSections: UnitService.getDisabledUpgradeSections(unit, upgradePackages)
    };
  }

  private static readonly countRegex = /^(\d+)x\s/;

  public static getDisabledUpgradeSections(u: IUnit, upgradePackages: IUpgradePackage[]) {
    const disabledSections: string[] = [];

    const packagesForUnit = u.upgrades
      // Map all upgrade packages
      .map((uid) => upgradePackages.find((pkg) => pkg.uid === uid))
      .filter(Boolean);

    let lastDisabledSectionsCount = -1;

    while (disabledSections.length > lastDisabledSectionsCount) {
      lastDisabledSectionsCount = disabledSections.length;

      const sections = packagesForUnit
        // Flatten down to array of all upgrade sections
        .reduce<IUpgradeSection[]>((sections, next) => sections.concat(next.sections), [])
        .filter((x) => !disabledSections.includes(x.uid));

      const allGains: IUpgradeGains[] = sections
        .reduce<IUpgradeOption[]>((opts, next) => opts.concat(next.options), [])
        .reduce<IUpgradeGains[]>((gains, next) => gains.concat(next.gains), [])
        .reduce<IUpgradeGains[]>((gains, next) => {
          // Add root item/weapon/etc
          gains.push(next);

          // For items, also add the content
          if (next.type !== GainType.Item) return gains;

          return gains.concat((next as IUpgradeGainsItem).content);
        }, []);

      const unitWeapons = getUnitWeapons(u);

      // For each section, check that the unit has access to the things it wants to replace
      // Only need sections that are replacing (or looking for) something
      for (let section of sections.filter((s) => s.targets)) {
        for (let what of section.targets) {
          const target = what.replace(this.countRegex, "");

          // Does weapons contain this thing?
          const weaponMatch = unitWeapons.some((e) => EquipmentService.compareEquipment(e, target));
          // If base weapon, then we won't be disabling this section...
          if (weaponMatch) continue;

          // Does a base unit item contain this thing?
          const itemMatch = u.items.some((e) => EquipmentService.compareEquipment(e, target));
          // If base item, then we won't be disabling this section...
          if (itemMatch) continue;

          // Do any upgrade sections contain this thing?
          const upgradeGains = allGains.find((g) => EquipmentService.compareEquipment(g, target));
          // If upgrade gains found, don't disable this
          if (upgradeGains) continue;

          console.log(what + " was not found");

          // If neither was found, then disable this section
          disabledSections.push(section.uid);
        }
      }
    }

    if (disabledSections.length > 0) {
      const allSections = packagesForUnit.flatMap((x) => x.sections);

      console.log("Disabled sections:", {
        unit: u.name,
        disabledSections: disabledSections.map((s) => {
          const section = allSections.find((sec) => sec.uid === s);
          const pkg = packagesForUnit.find((p) => p.uid === section.parentPackageUid);
          return `${pkg?.hint ?? "UNKNOWN PKG"}: ${section?.label ?? "UNKNOWN SECTION"}`;
        })
      });
    }

    return disabledSections;
  }

  public static unitsEqual(a: ISelectedUnit, b: ISelectedUnit) {
    if (a.id !== b.id) return false;
    if (a.selectionId !== b.selectionId) return false;
    if (a.customName !== b.customName) return false;
    if (a.joinToUnit !== b.joinToUnit) return false;
    if (a.combined !== b.combined) return false;
    if (a.xp !== b.xp) return false;
    if (a.selectedUpgrades.length !== b.selectedUpgrades.length) return false;
    for (let i = 0; i < a.selectedUpgrades.length; i++) {
      if (a.selectedUpgrades[i].instanceId !== b.selectedUpgrades[i].instanceId) return false;
    }
    return true;
  }
}
